import { questionBooklets } from "./bookletLock";

// Shared rules of the question page's review (ТЗ 3.2). The detail's `actions` say what the user may do now;
// these helpers only label and check what the page sends.

// The backend's limit of a comment (a decision, a resubmit, a vote).
export const COMMENT_LIMIT = 5000;

// What the old pages sent for an empty comment: the backend drops it, and old history records that say it
// have no comment.
const COMMENT_PLACEHOLDER = "no comment";

// A comment as the backend keeps it: trimmed, and the old placeholder is no comment.
export const cleanComment = (text) => {
  const value = typeof text === "string" ? text.trim() : "";
  return value.toLowerCase() === COMMENT_PLACEHOLDER ? "" : value;
};

// The comments of a history record worth showing (old records may say "no comment").
export const recordComments = (record) =>
  (record?.comments || []).map((comment) => cleanComment(comment?.content)).filter(Boolean);

// Older backends send no `actions`: the decisions their page offered, the comment needed to send a question back.
export const LEGACY_TRANSITIONS = ["APPROVED", "DECLINED", "CHANGES_REQUESTED", "ARCHIVED"];
export const LEGACY_COMMENT_REQUIRED = ["CHANGES_REQUESTED"];

// The steps the user may take now and those that need a comment. None while an active BTS booklet prints the
// question (the server turns them off too).
export function reviewSteps(question) {
  if (questionBooklets(question).length) return { transitions: [], commentRequired: [] };
  const actions = question?.actions;
  if (!actions) {
    const current = question?.status?.code;
    return {
      transitions: current === "DRAFT" ? [] : LEGACY_TRANSITIONS.filter((code) => code !== current),
      commentRequired: LEGACY_COMMENT_REQUIRED,
    };
  }
  return { transitions: actions.transitions || [], commentRequired: actions.comment_required || [] };
}

// The key of a step under question_detail.transitions (and question_detail.review.confirm): PENDING_REVIEW returns
// a question under review to the queue, and restores a declined or archived one.
export const transitionKey = (code, currentCode) =>
  code === "PENDING_REVIEW"
    ? currentCode === "UNDER_REVIEW"
      ? "pending_review_return"
      : "pending_review_restore"
    : String(code).toLowerCase();

// "subtopic, difficulty" of a `missing` list (an incomplete question), in the user's language.
export const missingFieldsText = (t, missing) =>
  (missing || []).map((field) => t(`question_fields.${field}`, { defaultValue: field })).join(", ");

export const statusLabel = (t, status) =>
  status?.code
    ? t(`question_detail.statuses.${status.code.toLowerCase()}`, { defaultValue: status.name || status.code })
    : status?.name || "";

export const userDisplayName = (t, user) => {
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ");
  return fullName || user?.username || user?.name || t("question_detail.roles.unknown_user");
};

// The newest status change of the history (newest first) to `code`.
export const latestStatusRecord = (history, code) =>
  (history || []).find((record) => record.type === "status_change" && record.status?.code === code) || null;

// The page the user came from inside the app, else `fallback`: a deleted question's page has nothing to show.
export const leavePage = (navigate, fallback) => {
  if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
  else navigate(fallback, { replace: true });
};
